import styled from '@emotion/styled';
import {
  faBrakeWarning,
  faCircleCheck,
  faFileCertificate,
  faLandmarkMagnifyingGlass,
} from '@fortawesome/duotone-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import type { DtoV1AUSGetAusDetailsResponse } from '~/__generated__/api';
import Box from '~/common/components/Box';
import Text from '~/common/components/Texts/Text';
import { AUS_METHOD } from '~/common/constants';
import { useGetAUSReportLinks } from '~/common/hooks/useGetAUSReportLinks';
import Button from '~/common/mui/Buttons/MUIButton/MUIButton';
import palette from '~/theme/mui/palette';

import { isAUSStatusSuccess, openAUSReport } from './ausUtils';
import useAUSStore from './useAUSStore';

type AUSLoaderScreenProps = {
  ausDetailsResponse?: DtoV1AUSGetAusDetailsResponse;
  dti?: string;
  hti?: string;
  ltv?: string;
  creditScore?: string;
};

const StyledStatHeading = styled(Text)`
  color: ${palette.black};
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1rem;
`;

const StyledStatValue = styled(Text)`
  font-weight: 500;
  font-size: 1rem;
  line-height: 1.5rem;
  color: ${palette.gray[900]};
`;

const AUSLoaderScreen: React.FC<Readonly<AUSLoaderScreenProps>> = ({
  ausDetailsResponse,
  dti = '',
  hti = '',
  ltv = '',
  creditScore = '',
}) => {
  const { isAUSRunSuccessful } = useAUSStore();
  const lastAusRunDetails = ausDetailsResponse?.lastAusRunDetails ?? {};

  const rawLastRunAUS = lastAusRunDetails.requestMethod;
  const lastRunAUS =
    typeof rawLastRunAUS === 'number'
      ? (AUS_METHOD[rawLastRunAUS] ?? 'DU')
      : (rawLastRunAUS ?? 'DU');
  const ausType = `${lastRunAUS} Run Complete`;
  const runDate = lastAusRunDetails.requestShortDate ?? '';
  const runTime = lastAusRunDetails.requestTime ?? '';
  const runStatus = lastAusRunDetails.requestStatus ?? '';
  const runRecommendation = lastAusRunDetails.recommendation ?? '';
  const isSuccess = isAUSStatusSuccess(runRecommendation);

  const allowedStatuses = ['Processed Data'];
  const isReportEnabled = allowedStatuses.includes(runStatus);

  const statusIcon = isSuccess ? faCircleCheck : faBrakeWarning;
  const statusIconColor = isSuccess ? palette.green[500] : palette.red[500];
  const statusBgColor = isSuccess ? palette.green[100] : palette.red[100];

  const [findingsLoading, setFindingsLoading] = useState(false);
  const [creditLoading, setCreditLoading] = useState(false);

  const findingsQuery = useGetAUSReportLinks({
    requestId: lastAusRunDetails?.requestIdentifier?.requestId,
    ausMethod: lastAusRunDetails?.requestMethod,
    requestDateTime: lastAusRunDetails?.requestDate,
    reportType: 'findings',
    requestStatus: lastRunAUS === 'LPA' ? isReportEnabled : null,
    enabled: false,
  });
  const creditQuery = useGetAUSReportLinks({
    requestId: lastAusRunDetails?.requestIdentifier?.requestId,
    ausMethod: lastAusRunDetails?.requestMethod,
    requestDateTime: lastAusRunDetails?.requestDate,
    reportType: 'credit',
    requestStatus: null,
    enabled: false,
  });

  const handleFindingsClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    setFindingsLoading(true);
    await openAUSReport(findingsQuery.refetch, 'DU Findings');
    setFindingsLoading(false);
  };

  const handleCreditClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    setCreditLoading(true);
    await openAUSReport(creditQuery.refetch, 'Credit Report');
    setCreditLoading(false);
  };

  if (isAUSRunSuccessful) {
    return (
      <Box
        sx={{
          minHeight: '100%',
          background: palette.white,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: 0,
        }}
        aria-label="AUS Result Screen"
      >
        {/* Section 1: AUS Result */}
        <Box
          sx={{
            width: '100%',
            maxWidth: '501px',
            background: palette.gray[50],
            borderRadius: '8px',
            border: `1px solid ${palette.gray[200]}`,
            padding: '1.5rem 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Text
            as="h2"
            sx={{
              fontWeight: 500,
              fontSize: '1.5rem',
              lineHeight: '2rem',
              color: palette.blue[900],
              mb: 4,
            }}
          >
            {ausType}
          </Text>
          <Text
            sx={{
              color: palette.black,
              fontSize: '1rem',
              lineHeight: '1.5rem',
              fontWeight: 400,
            }}
          >
            {runDate}
          </Text>
          <Text
            sx={{
              color: palette.gray[500],
              fontSize: '0.75rem',
              lineHeight: '1rem',
              fontWeight: 400,
            }}
          >
            {runTime}
          </Text>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              margin: '2rem 0',
              background: statusBgColor,
              borderRadius: '4px',
              padding: '8px 12px',
              color: palette.black,
              justifyContent: 'center',
            }}
          >
            <FontAwesomeIcon
              icon={statusIcon}
              style={{
                fontSize: '1.5rem',
                marginRight: '0.5rem',
                color: statusIconColor,
              }}
            />
            <Text as="span" sx={{ fontWeight: 400, fontSize: '1.1rem' }}>
              {runRecommendation}
            </Text>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 4,
              width: '100%',
            }}
          >
            <Button
              variant="outlined"
              endIcon={
                <FontAwesomeIcon
                  icon={faLandmarkMagnifyingGlass}
                  color={palette.blue[700]}
                  style={{ marginLeft: 8, fontSize: '1.2rem' }}
                />
              }
              onClick={handleFindingsClick}
              isLoading={findingsLoading}
            >
              {lastRunAUS} Findings
            </Button>
            <Button
              variant="outlined"
              endIcon={
                <FontAwesomeIcon
                  icon={faFileCertificate}
                  color={palette.blue[700]}
                  style={{ marginLeft: 8, fontSize: '1.2rem' }}
                />
              }
              onClick={handleCreditClick}
              isLoading={creditLoading}
              disabled={!isReportEnabled}
            >
              Credit Report
            </Button>
          </Box>
        </Box>

        {/* Section 2: Reference Data */}
        <Box
          sx={{
            width: '100%',
            maxWidth: '501px',
            mt: '1.5rem',
            background: palette.gray[50],
            borderRadius: '8px',
            border: `1px solid ${palette.gray[200]}`,
            padding: '1rem 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Text as="h3" sx={{ fontWeight: 500, mb: 4, color: palette.black }}>
            For reference
          </Text>
          <Box
            sx={{
              display: 'flex',
              gap: 8,
              width: '100%',
              justifyContent: 'center',
            }}
          >
            <Box>
              <StyledStatHeading>HTI</StyledStatHeading>
              <StyledStatValue>{hti}</StyledStatValue>
            </Box>
            <Box>
              <StyledStatHeading>DTI</StyledStatHeading>
              <StyledStatValue>{dti}</StyledStatValue>
            </Box>
            <Box>
              <StyledStatHeading>LTV</StyledStatHeading>
              <StyledStatValue>{ltv}</StyledStatValue>
            </Box>
            <Box>
              <StyledStatHeading>Credit Score</StyledStatHeading>
              <StyledStatValue>{creditScore}</StyledStatValue>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
  // Fallback (should not render)
  return null;
};

export default AUSLoaderScreen;
